/* eslint-disable complexity */
import React, { useCallback, useEffect, useState } from 'react';
import { addDays, addYears } from 'date-fns';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

import { FormattedMessage, getCurrencySymbolFromCode } from '@futrli/localisation';
import { AnyObject } from '@futrli/shared-services';
import { PreviewAssumptionImpactsResponse, RepeatingAssumption } from '@futrli-sdk/predict';
import { RepeatingAssumption as ConsolidationRepeatingAssumption } from '@futrli-sdk/consolidations';

import { BLACKLIST_NUMBER_VALUES } from '../../../../core/constants';
import {
  EndDateType,
  FormulaError,
  IncreaseDecreaseRepeating,
  PredictionHeaderProps,
  RepeatingPattern
} from '../../types';
import { formatDate } from '../../../../core/formatters';
import { forceLocalDate } from '../../../../utils/dates';
import { RepeatingMethodState } from './types';
import { RepeatingMethodTopBar } from './components/repeating-method-top-bar';
import styles from './repeating-method.scss';

interface Props {
  currency?: string;
  PredictionHeader: React.FunctionComponent<PredictionHeaderProps>;
  isFormSubmitted: boolean;
  onChange: (isValid: boolean, data: AnyObject) => void;
  repeatingMethodData: RepeatingMethodState;
  previewImpacts: PreviewAssumptionImpactsResponse | undefined;
}

type ErrorState<T> = {
  [P in keyof T]?: JSX.Element;
};

export const getInitialRepeatingState = (forecastFromDate?: string | null): RepeatingMethodState => ({
  amount: '',
  repeat_pattern: RepeatingPattern.monthly,
  start_date: forecastFromDate ? forceLocalDate(forecastFromDate) : new Date(Date.now()),
  increase_decrease_type: IncreaseDecreaseRepeating.never,
  end_type: EndDateType.end_never,
  end_on_date: forecastFromDate ? addYears(forceLocalDate(forecastFromDate), 1) : addYears(Date.now(), 1),
  end_after_repeat_count: '12',
  end_after_repeat_type: 'repeats',
  increase_decrease_percentage: '',
  increase_decrease_fixed_amount: ''
});

export const initializePredictionDetailsState = (
  predictionDetails: RepeatingAssumption | ConsolidationRepeatingAssumption
) => {
  return {
    amount: predictionDetails?.amount || '',
    repeat_pattern: (predictionDetails?.repeat_pattern as RepeatingPattern) || 'monthly',
    start_date: predictionDetails?.start_date ? forceLocalDate(predictionDetails.start_date) : new Date(Date.now()),
    increase_decrease_type: (predictionDetails?.increase_decrease_type as IncreaseDecreaseRepeating) || null,
    end_type: (predictionDetails?.end_type as EndDateType) || 'never',
    end_on_date: predictionDetails?.end_on_date
      ? forceLocalDate(predictionDetails.end_on_date)
      : addYears(Date.now(), 1),
    end_after_repeat_count: predictionDetails?.end_after_repeat_count
      ? predictionDetails.end_after_repeat_count.toString()
      : '12',
    end_after_repeat_type: predictionDetails?.end_after_repeat_type || 'repeats',
    increase_decrease_percentage: predictionDetails?.increase_decrease_percentage || '',
    increase_decrease_fixed_amount: predictionDetails?.increase_decrease_fixed_amount || '',
    vat_percentage: predictionDetails?.vat_percentage || ''
  };
};

export const invalidNumberMessage = (
  <FormattedMessage id="common.errorMsg.invalidNumber" defaultMessage="Must be a valid number" />
);

const isValidAmount = (amount: string): boolean => {
  return !(!amount || (amount && BLACKLIST_NUMBER_VALUES.includes(amount)) || parseFloat(amount) === 0);
};

const isValidNumber = (value: string): boolean => {
  return !(!value || BLACKLIST_NUMBER_VALUES.includes(value));
};

export const RepeatingMethod = ({
  PredictionHeader,
  currency,
  repeatingMethodData,
  onChange,
  isFormSubmitted,
  previewImpacts
}: Props) => {
  const [predictionDetails, setPredictionDetails] = useState<RepeatingMethodState>(() => ({ ...repeatingMethodData }));
  const [errors, setErrors] = useState<ErrorState<RepeatingMethodState>>();

  useEffect(() => {
    if (!isEqual(predictionDetails, repeatingMethodData)) {
      setPredictionDetails(repeatingMethodData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repeatingMethodData]);

  useEffect(() => {
    onChange(isEmpty(validate()), formatPendingData());
  }, []); // eslint-disable-line

  useEffect(() => {
    const validationErrors = validate();

    if (isFormSubmitted && !isEqual(errors, validationErrors)) {
      setErrors(validationErrors);
    }

    onChange(isEmpty(validationErrors), formatPendingData());
  }, [predictionDetails, errors, isFormSubmitted]); // eslint-disable-line

  const getNextDay = (date: Date) => {
    const newDate = new Date(date);

    return addDays(newDate, 1);
  };

  const formatPendingData = () => {
    return {
      ...predictionDetails,
      errors: errors || {},
      start_date:
        typeof predictionDetails.start_date === 'string'
          ? predictionDetails.start_date
          : formatDate(predictionDetails.start_date),
      increase_decrease_type: predictionDetails.increase_decrease_type || 'no-change',
      increase_decrease_percentage:
        predictionDetails.increase_decrease_type === 'percentage'
          ? predictionDetails.increase_decrease_percentage
          : null,
      increase_decrease_fixed_amount:
        predictionDetails.increase_decrease_type === 'fixed-amount'
          ? predictionDetails.increase_decrease_fixed_amount
          : null,
      end_on_date:
        predictionDetails.end_type === 'on-date' && predictionDetails.end_on_date
          ? typeof predictionDetails.end_on_date === 'string'
            ? predictionDetails.end_on_date
            : formatDate(predictionDetails.end_on_date)
          : null,
      end_after_repeat_count:
        predictionDetails.end_type === 'after-repeats' ? Number(predictionDetails.end_after_repeat_count) : null, // added Number due the type from sdk-predict has changed
      end_after_repeat_type:
        predictionDetails.end_type === 'after-repeats' ? predictionDetails.end_after_repeat_type : 'repeats'
    };
  };

  const validate = (): ErrorState<RepeatingMethodState> => {
    const {
      amount,
      end_after_repeat_count,
      end_type,
      increase_decrease_fixed_amount,
      increase_decrease_percentage,
      increase_decrease_type
    } = predictionDetails;
    const errorState: ErrorState<RepeatingMethodState> = {};

    if (!isValidAmount(amount)) {
      errorState.amount = invalidNumberMessage;
    }

    if (end_type === 'after-repeats' && !isValidNumber(end_after_repeat_count)) {
      errorState.end_after_repeat_count = invalidNumberMessage;
    }

    if (
      increase_decrease_type === 'fixed-amount' &&
      (!increase_decrease_fixed_amount ||
        (increase_decrease_fixed_amount && !isValidNumber(increase_decrease_fixed_amount)))
    ) {
      errorState.increase_decrease_fixed_amount = invalidNumberMessage;
    }

    if (
      increase_decrease_type === 'percentage' &&
      (!increase_decrease_percentage || (increase_decrease_percentage && !isValidNumber(increase_decrease_percentage)))
    ) {
      errorState.increase_decrease_percentage = invalidNumberMessage;
    }

    return errorState;
  };

  const handleStartDateChange = (date: Date) => {
    const newState = { ...predictionDetails, start_date: date };
    if (predictionDetails.end_on_date && date >= predictionDetails.end_on_date) {
      newState.end_on_date = getNextDay(date);
    }
    setPredictionDetails(newState);
  };

  const handleRepeatingPatternChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newState = { ...predictionDetails, repeat_pattern: event.target.value as RepeatingPattern };
      setPredictionDetails(newState);
    },
    [predictionDetails]
  );

  const handleEndDateTypeChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newState = { ...predictionDetails, end_type: event.target.value as EndDateType };
    setPredictionDetails(newState);
  };

  // end_after_repeat_count
  const handleEndsAfterChange = (value: string) => {
    const newState = { ...predictionDetails, end_after_repeat_count: value };
    setPredictionDetails(newState);
  };

  const handleEndDateChange = (date: Date) => {
    const newState = { ...predictionDetails, end_on_date: date };
    setPredictionDetails(newState);
  };

  // increase_decrease_type
  const handleIncreaseDecreaseRepeatingType = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newState = { ...predictionDetails, increase_decrease_type: e.target.value as IncreaseDecreaseRepeating };
    setPredictionDetails(newState);
  };

  const handleIncreaseDecreaseRepeatingAmount = (value: string) => {
    if (predictionDetails.increase_decrease_type === IncreaseDecreaseRepeating.increaseDecreaseByAmount) {
      const newState = {
        ...predictionDetails,
        increase_decrease_fixed_amount: value,
        increase_decrease_percentage: undefined
      };
      setPredictionDetails(newState);
    }
    if (predictionDetails.increase_decrease_type === IncreaseDecreaseRepeating.increaseDecreaseByPercentage) {
      const newState = {
        ...predictionDetails,
        increase_decrease_percentage: value,
        increase_decrease_fixed_amount: undefined
      };
      setPredictionDetails(newState);
    }
  };

  const handleRepeatingAmountChange = (value: string) => {
    const newState = { ...predictionDetails, amount: value };
    setPredictionDetails(newState);
  };

  return (
    <div>
      <RepeatingMethodTopBar
        currencySymbol={getCurrencySymbolFromCode(currency!)}
        data={predictionDetails}
        onEndDateChange={handleEndDateChange}
        onEndDateTypeChange={handleEndDateTypeChange}
        onEndsAfterChange={handleEndsAfterChange}
        onIncreaseDecreaseAmountChange={handleIncreaseDecreaseRepeatingAmount}
        onIncreaseDecreaseRepeatingTypeChange={handleIncreaseDecreaseRepeatingType}
        onRepeatingAmountChange={handleRepeatingAmountChange}
        onRepeatingPatternChange={handleRepeatingPatternChange}
        onStartDateChange={handleStartDateChange}
        PredictionHeader={PredictionHeader}
      />
      {!previewImpacts?.is_valid && previewImpacts?.error_code === FormulaError.ERROR_VALUES_OUT_OF_EXPECTED_RANGE && (
        <div className={styles.formulaError}>
          <FormattedMessage
            id="predict.predict_error_values_out_of_expected_range"
            defaultMessage="Prediction cannot be saved, as it produces values out of expected range."
          />
        </div>
      )}
    </div>
  );
};
